import FieldLabel from './ui/FieldLabel'
import Textarea from './ui/Textarea'
import UiInput from './ui/Input'
import Button from './ui/Button'
import React, { useEffect, useRef, useState } from "react";
import ModalComponent from "./Modal";

export default function ScopeOfWorks({
    name,
    label,
    required,
    value,
    setValue,
    errorMessage,
    disabled,
    placeholder,
    quickPhrases = [],
    allowManage = false,
    onAddPhrase,
    onRemovePhrase,
    onUpdatePhrase,
    onDraftChange,
    commitOnBlurOnly = false,
    hideFieldLabel = false,
    // When provided, the quick-phrase chips span the full width and the textarea
    // sits beside `sideSlot` in a two-column grid (matches the reference design).
    sideSlot
}) {
    const textareaRef = useRef(null);
    const commitTimerRef = useRef(null);
    const [showManage, setShowManage] = useState(false);
    const [draftValue, setDraftValue] = useState(value || "");
    const [newPhrase, setNewPhrase] = useState("");
    // One add at a time: a second click while saving would add the phrase twice.
    const savingPhraseRef = useRef(false);
    const [savingPhrase, setSavingPhrase] = useState(false);
    const [confirmRemove, setConfirmRemove] = useState(null);
    const [managedPhrases, setManagedPhrases] = useState([]);

    useEffect(() => {
        clearTimeout(commitTimerRef.current);
        setDraftValue(value || "");
    }, [value]);

    useEffect(() => () => clearTimeout(commitTimerRef.current), []);

    const commitValue = (nextValue) => {
        clearTimeout(commitTimerRef.current);
        if (nextValue !== value) setValue(nextValue);
    };

    const handleValueChange = (nextValue) => {
        setDraftValue(nextValue);
        onDraftChange?.(nextValue);
        clearTimeout(commitTimerRef.current);
        if (!commitOnBlurOnly) commitTimerRef.current = setTimeout(() => setValue(nextValue), 250);
    };

    const phrases = quickPhrases.map((phrase) => {
        if (typeof phrase === "string") {
            return { id: phrase, text: phrase, removable: true };
        }
        return {
            id: phrase.id || phrase.phrase || phrase.text,
            text: phrase.text || phrase.phrase || "",
            removable: true
        };
    });

    useEffect(() => {
        if (!showManage) return;
        setManagedPhrases(
            phrases.map((phrase) => ({
                id: phrase.id,
                text: phrase.text,
                originalText: phrase.text
            }))
        );
    }, [showManage, quickPhrases]);

    const insertPhrase = (text) => {
        const el = textareaRef.current;
        if (!el) return;
        clearTimeout(commitTimerRef.current);

        const start = el.selectionStart;
        const end = el.selectionEnd;

        const before = draftValue.substring(0, start);
        const after = draftValue.substring(end);

        const newValue = `${before}${text}\n ${after}`;
        setDraftValue(newValue);
        onDraftChange?.(newValue);
        setValue(newValue);

        // reposition cursor
        const cursorPos = start + text.length + 1;

        setTimeout(() => {
            el.focus();
            el.setSelectionRange(cursorPos, cursorPos);
        }, 0);
    };

    const handleSavePhrase = async () => {
        const trimmed = newPhrase.trim();
        if (!trimmed || savingPhraseRef.current) return;
        savingPhraseRef.current = true;
        setSavingPhrase(true);
        try {
            if (onAddPhrase) {
                await onAddPhrase(trimmed);
            }
            setNewPhrase("");
            setShowManage(true);
        } catch (e) {
            return;
        } finally {
            savingPhraseRef.current = false;
            setSavingPhrase(false);
        }
    };

    const handleUpdatePhrase = async (phrase) => {
        const trimmed = phrase.text.trim();
        if (!trimmed || trimmed === phrase.originalText) {
            return;
        }
        try {
            if (onUpdatePhrase) {
                await onUpdatePhrase({ id: phrase.id, text: trimmed });
            }
            setManagedPhrases((prev) =>
                prev.map((item) => item.id === phrase.id
                    ? { ...item, text: trimmed, originalText: trimmed }
                    : item
                )
            );
        } catch (e) {
            return;
        }
    };

    const field = (
        <div>
            {!hideFieldLabel && <FieldLabel className="scopeFieldLabel">{label}</FieldLabel>}
            <Textarea
                ref={textareaRef}
                name={name}
                disabled={disabled}
                placeholder={placeholder || ""}
                value={draftValue}
                onChange={(e) => handleValueChange(e.target.value)}
                onBlur={() => commitValue(draftValue)}
                className="scopeTextarea"
                style={errorMessage ? { borderColor: "red" } : undefined}
            />
            {errorMessage && (
                <p style={{ margin: 0, fontSize: "12px", color: "red" }}>
                    {errorMessage}
                </p>
            )}
        </div>
    );

    const phraseColumnSize = Math.max(1, Math.ceil(managedPhrases.length / 3));
    const phraseColumns = Array.from({ length: 3 }, (_, columnIndex) =>
        managedPhrases.slice(columnIndex * phraseColumnSize, (columnIndex + 1) * phraseColumnSize)
    );

    return (
        <div>
            {/* LABEL */}
            <FieldLabel className="scopeFieldLabel scopeSectionLabel">
                {label} {required && <span className="uiRequired" style={{ color: "#dc2626" }}>*</span>}
            </FieldLabel>

            {/* QUICK PHRASES */}
            <div className="scopePhraseRow">
                {phrases.map((phrase) => (
                    <button
                        key={phrase.id || phrase.text}
                        type="button"
                        className="scopePhraseChip"
                        onClick={() => insertPhrase(phrase.text)}
                    >
                        {phrase.text}
                    </button>
                ))}
                {allowManage && !disabled && (
                    <Button data-action-tone="edit" variant="secondary" size="sm"
                        type="button"
                        className="scopePhraseChip scopePhraseChipEdit"
                        onClick={() => setShowManage(true)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" aria-hidden="true">
                            <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
                        </svg>
                        Edit
                    </Button>
                )}
            </div>

            {sideSlot ? (
                <div className="allocationDetailsGrid">
                    {field}
                    {sideSlot}
                </div>
            ) : field}

            <ModalComponent
                showModal={!!showManage}
                setShowModal={setShowManage}
                customMaxWidth="920px"
                customContentStyle={{ padding: 0, width: "92%", overflow: "hidden" }}
            >
                <div className="scopePhraseModal">
                    <div className="scopePhraseModalHeader">
                        <h2 className="modalTitle">Manage Scope Phrases</h2>
                    </div>

                    <div className="scopePhraseModalBody">
                        <div className="scopePhraseEditorGrid">
                            {phraseColumns.map((column, columnIndex) => (
                                <div className="scopePhraseEditorColumn" key={`scope-column-${columnIndex}`}>
                                    <div className="scopePhraseEditorHeading">
                                        <span>Phrase</span>
                                        <span>Action</span>
                                    </div>
                                    {column.map((phrase) => (
                                        <div className="scopePhraseEditorRow" key={phrase.id}>
                                            <UiInput
                                                value={phrase.text}
                                                onChange={(e) => {
                                                    const next = e.target.value;
                                                    setManagedPhrases((prev) =>
                                                        prev.map((item) => item.id === phrase.id ? { ...item, text: next } : item)
                                                    );
                                                }}
                                                onBlur={() => handleUpdatePhrase(phrase)}
                                            />
                                            <div className="scopePhraseEditorActions">
                                                <Button data-action-tone="delete" variant="danger" size="md"
                                                    type="button"
                                                    className="scopePhraseDeleteButton"
                                                    onClick={() => setConfirmRemove({ id: phrase.id, text: phrase.text })}
                                                    aria-label={`Delete ${phrase.text}`}
                                                    title="Delete"
                                                >
                                                    Delete
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>

                        <div className="scopePhraseAddArea">
                            <UiInput
                                value={newPhrase}
                                onChange={(e) => setNewPhrase(e.target.value)}
                                placeholder="New phrase"
                            />
                            <Button variant="create" size="md"
                                type="button"
                                className="scopePhraseAddButton"
                                onClick={handleSavePhrase}
                                disabled={!newPhrase.trim() || savingPhrase}
                            >
                                {savingPhrase ? "Adding..." : "Add"}
                            </Button>
                        </div>
                    </div>

                    <div className="scopePhraseModalFooter">
                        <Button variant="secondary" size="md" className="secondaryButton" type="button" onClick={() => setShowManage(false)}>
                            Cancel
                        </Button>
                    </div>
                </div>
            </ModalComponent>

            <ModalComponent
                showModal={!!confirmRemove}
                setShowModal={setConfirmRemove}
                customMaxWidth="420px"
                customContentStyle={{ padding: 0, width: "90%", overflow: "hidden", boxShadow: "none" }}
            >
                <div className="scopePhraseDeleteModal">
                    <div className="scopePhraseDeleteModalBody">
                        <h2 className="modalTitle">Delete this phrase?</h2>
                        <p>This phrase will be removed from the Scope of Works options.</p>
                    </div>
                    <div className="scopePhraseDeleteModalFooter">
                        <Button data-action-tone="cancel" variant="secondary" size="md"  className="secondaryButton scopePhraseDeleteModalButton" type="button" onClick={() => setConfirmRemove(null)}>
                            Cancel
                        </Button>
                        <Button data-action-tone="delete" variant="danger" size="md"
                            className="modalDangerButton scopePhraseDeleteModalButton"
                            type="button"
                            onClick={async () => {
                                try {
                                    if (onRemovePhrase && confirmRemove) {
                                        await onRemovePhrase(confirmRemove);
                                    }
                                    setConfirmRemove(null);
                                } catch (e) {
                                    return;
                                }
                            }}
                        >
                            Delete
                        </Button>
                    </div>
                </div>
            </ModalComponent>
        </div>
    );
}
